import axios from 'axios';
import { authClient } from '../auth/authClient';

// API calls go through local backend.
//
// Same-origin '/api' is the default and what production uses (the server serves
// client/build, so there is no cross-origin hop). REACT_APP_API_BASE overrides
// it for local development, where the CRA dev-server proxy is not always usable
// — in a dual-stack devcontainer `localhost` resolves to ::1 while the API binds
// IPv4, and http-proxy-middleware stalls rather than failing over. Pointing the
// browser straight at the API sidesteps the proxy entirely; the server sets
// cors({ origin: true, credentials: true }), so the direct call is accepted.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: inject Authorization header + tenant ID
api.interceptors.request.use(async (config) => {
  const token = await authClient.getValidAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = authClient.getActiveTenantId();
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  return config;
});

// Response interceptor: handle 401 with auto-refresh, general errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet, try refreshing the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const newToken = await authClient.handle401();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
      // handle401 will redirect to login if refresh fails
      return Promise.reject(error);
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ────────────────────────────────────────────────────────────────
// Dashboard Service (iot-admin's own API)
// ────────────────────────────────────────────────────────────────
export const dashboardService = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },

  getActivity: async () => {
    const response = await api.get('/dashboard/activity');
    return response.data;
  },

  getAlerts: async () => {
    const response = await api.get('/dashboard/alerts');
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Device Service (iot-admin's own API) - Edge Nodes Devices
// ────────────────────────────────────────────────────────────────
export const deviceService = {
  getAll: async (params = {}) => {
    const response = await api.get('/devices', { params });
    return response.data;
  },

  getById: async (deviceId, params = {}) => {
    const response = await api.get(`/devices/${deviceId}`, { params });
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/devices', data);
    return response.data;
  },

  update: async (deviceId, data) => {
    const response = await api.put(`/devices/${deviceId}`, data);
    return response.data;
  },

  delete: async (deviceId) => {
    const response = await api.delete(`/devices/${deviceId}`);
    return response.data;
  },

  // Tenant assignment. Assigning to a tenant not enrolled in media_console is a
  // 409 with code PRODUCT_NOT_ENROLLED (V276); the caller may retry with
  // opts { enroll_media_console: true } to enroll-and-assign in one step.
  assignTenant: async (deviceId, tenantId, opts = {}) => {
    const response = await api.post(`/devices/${deviceId}/assign`, { tenantId, ...opts });
    return response.data;
  },

  unassignTenant: async (deviceId) => {
    const response = await api.post(`/devices/${deviceId}/unassign`);
    return response.data;
  },

  getTelemetry: async (deviceId, params = {}) => {
    const response = await api.get(`/devices/${deviceId}/events`, { params });
    return response.data;
  },

  sendCommand: async (deviceId, command, payload) => {
    const response = await api.post(`/devices/${deviceId}/command`, { command, payload });
    return response.data;
  },

  // GGOS Registration
  triggerGGOSRegistration: async (deviceId) => {
    const response = await api.post(`/devices/${deviceId}/trigger-ggos-registration`);
    return response.data;
  },

  // ── Device documents (SIM card / envelope / number plate / driver images) ──
  getDocUploadUrl: async (deviceId, docType, contentType) => {
    const response = await api.post(`/devices/${deviceId}/documents/upload-url`, { docType, contentType });
    return response.data;
  },

  // Full flow: mint presigned PUT → upload file straight to S3 → return the key.
  // The S3 PUT must NOT carry the app Authorization header (it would break the
  // presigned signature), so we use fetch() instead of the axios instance.
  uploadDocument: async (deviceId, docType, file) => {
    const { data } = await deviceService.getDocUploadUrl(deviceId, docType, file.type);
    const putRes = await fetch(data.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
    return data.key;
  },

  // Persist a device-level document key (sim_card | sim_card_envelope | hardware_label).
  // The number plate is NOT here any more — it belongs to the vehicle (V250).
  saveDocument: async (deviceId, docType, key) => {
    const response = await api.put(`/devices/${deviceId}/documents`, { docType, key });
    return response.data;
  },

  // ── Drivers (one active per device, history retained) ──
  getDrivers: async (deviceId) => {
    const response = await api.get(`/devices/${deviceId}/drivers`);
    return response.data;
  },

  assignDriver: async (deviceId, driver) => {
    const response = await api.post(`/devices/${deviceId}/drivers`, driver);
    return response.data;
  },

  updateDriver: async (deviceId, driverId, driver) => {
    const response = await api.put(`/devices/${deviceId}/drivers/${driverId}`, driver);
    return response.data;
  },

  unassignDriver: async (deviceId, driverId) => {
    const response = await api.post(`/devices/${deviceId}/drivers/${driverId}/unassign`);
    return response.data;
  },

  // ── Vehicle (which car this device is fitted in) ──
  // Link only: the body carries a vehicleId and nothing about the car, so a
  // device page can never edit a car's particulars by accident.
  fitVehicle: async (deviceId, vehicleId) => {
    const response = await api.post(`/devices/${deviceId}/vehicle`, { vehicleId });
    return response.data;
  },

  unfitVehicle: async (deviceId) => {
    const response = await api.post(`/devices/${deviceId}/vehicle/unfit`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Vehicle Service (iot-admin's own API)
// ────────────────────────────────────────────────────────────────
// Cars are a first-class entity since V250. Before it a car was the device's
// NAME plus a photo of its plate, so none of these calls had anywhere to live.
export const vehicleService = {
  // Same endpoint serves the listing and the device page's fit picker.
  list: async (params = {}) => {
    const response = await api.get('/vehicles', { params });
    return response.data;
  },

  search: async (search) => {
    const response = await api.get('/vehicles', { params: { search, pageSize: 10 } });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/vehicles/${id}`);
    return response.data;
  },

  // A duplicate registration is a 409 carrying `data.existingVehicleId`. Because
  // registration_number is a generated column, that fires for ANY spelling of a
  // plate we already hold — 'MH 03 EG 6307' will not sneak past 'MH03EG6307'.
  create: async (payload) => {
    const response = await api.post('/vehicles', payload);
    return response.data;
  },

  // Particulars and document expiry dates. Image keys go through saveDocument
  // so an upload cannot be lost to a concurrent edit of the registration.
  update: async (id, payload) => {
    const response = await api.put(`/vehicles/${id}`, payload);
    return response.data;
  },

  // Refused (409) while any fitting exists, past ones included — a fitting is
  // the record of where a screen was serving.
  remove: async (id) => {
    const response = await api.delete(`/vehicles/${id}`);
    return response.data;
  },

  // ── Documents (RC / insurance / PUC / fitness / permit / plate) ──
  // Vehicle-scoped, not device-scoped: an RC belongs to the car and must
  // survive the tablet being swapped out.
  uploadDocument: async (vehicleId, docType, file) => {
    const res = await api.post(`/vehicles/${vehicleId}/documents/upload-url`, {
      docType, contentType: file.type,
    });
    const { data } = res.data;
    // The S3 PUT must not carry the app Authorization header — it would break
    // the presigned signature — so this uses fetch(), not the axios instance.
    const putRes = await fetch(data.uploadUrl, {
      method: 'PUT', body: file, headers: { 'Content-Type': file.type },
    });
    if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
    return data.key;
  },

  // key=null clears the document. An expiry may ride along, since uploading a
  // fresh certificate and recording when it lapses are one act to the user.
  saveDocument: async (vehicleId, docType, key, expiresOn) => {
    const response = await api.put(`/vehicles/${vehicleId}/documents`, { docType, key, expiresOn });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Driver Service (iot-admin's own API)
// ────────────────────────────────────────────────────────────────
// Drivers are a first-class entity since V249 — a PERSON, not a row per
// assignment — so they get their own service rather than hanging off
// deviceService. The search below is not device-scoped at all.
export const driverService = {
  // Find an existing person so the assign flow can LINK them instead of
  // retyping a name and creating a duplicate human.
  search: async (search) => {
    const response = await api.get('/drivers', { params: { search } });
    return response.data;
  },

  // The listing. Same endpoint as search() — `search` alone returns the picker
  // shape, paging/filter params return the listing shape.
  list: async (params = {}) => {
    const response = await api.get('/drivers', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/drivers/${id}`);
    return response.data;
  },

  // Creates a PERSON with no cab. Assignment is a separate act, on the device.
  // A duplicate mobile is a 409 carrying `data.existingDriverId` — the caller is
  // expected to offer that driver rather than force a second record of one human.
  create: async (payload) => {
    const response = await api.post('/drivers', payload);
    return response.data;
  },

  // Edits the person everywhere: every cab and their whole history. Not a
  // device-scoped edit — that distinction is the entire point of V249.
  update: async (id, payload) => {
    const response = await api.put(`/drivers/${id}`, payload);
    return response.data;
  },

  // Refused (409) while any assignment exists, past ones included — history is
  // evidence of who drove what, and the FK is ON DELETE RESTRICT besides.
  remove: async (id) => {
    const response = await api.delete(`/drivers/${id}`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Device Group Service (iot-admin's own API)
// ────────────────────────────────────────────────────────────────
export const deviceGroupService = {
  getAll: async (params = {}) => {
    const response = await api.get('/device-groups', { params });
    return response.data;
  },

  getById: async (groupId) => {
    const response = await api.get(`/device-groups/${groupId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/device-groups', data);
    return response.data;
  },

  update: async (groupId, data) => {
    const response = await api.put(`/device-groups/${groupId}`, data);
    return response.data;
  },

  delete: async (groupId) => {
    const response = await api.delete(`/device-groups/${groupId}`);
    return response.data;
  },

  // Device membership
  getDevices: async (groupId, params = {}) => {
    const response = await api.get(`/device-groups/${groupId}/devices`, { params });
    return response.data;
  },

  addDevices: async (groupId, deviceIds) => {
    const response = await api.post(`/device-groups/${groupId}/devices`, { deviceIds });
    return response.data;
  },

  removeDevice: async (groupId, deviceId) => {
    const response = await api.delete(`/device-groups/${groupId}/devices/${deviceId}`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Settings Service (iot-admin's own API)
// ────────────────────────────────────────────────────────────────
export const settingsService = {
  get: async () => {
    const response = await api.get('/settings');
    return response.data;
  },

  update: async (settings) => {
    const response = await api.put('/settings', settings);
    return response.data;
  },

  getUsers: async () => {
    const response = await api.get('/settings/users');
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post('/settings/users', userData);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Access Policy Service (V277 door policy — iot-admin's own API)
// ────────────────────────────────────────────────────────────────
export const accessPolicyService = {
  getAll: async () => {
    const response = await api.get('/access-policies');
    return response.data;
  },

  // note replaces the stored note (it explains the CURRENT mode).
  update: async (door, { mode, note }) => {
    const response = await api.put(`/access-policies/${door}`, { mode, note });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// User Service (Cognito + RDS user management)
// ────────────────────────────────────────────────────────────────
export const userService = {
  getAll: async (params = {}) => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  getById: async (username) => {
    const response = await api.get(`/users/${encodeURIComponent(username)}`);
    return response.data;
  },

  create: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  update: async (username, userData) => {
    const response = await api.put(`/users/${encodeURIComponent(username)}`, userData);
    return response.data;
  },

  delete: async (username) => {
    const response = await api.delete(`/users/${encodeURIComponent(username)}`);
    return response.data;
  },

  disable: async (username) => {
    const response = await api.put(`/users/${encodeURIComponent(username)}/disable`);
    return response.data;
  },

  enable: async (username) => {
    const response = await api.put(`/users/${encodeURIComponent(username)}/enable`);
    return response.data;
  },

  // Invite-only approval (V248). These take the user's UUID, not the email the
  // enable/disable pair accepts: the decision is proxied to the login service,
  // which addresses users by id only.
  approve: async (id) => {
    const response = await api.put(`/users/${encodeURIComponent(id)}/approve`);
    return response.data;
  },

  reject: async (id, note) => {
    const response = await api.put(`/users/${encodeURIComponent(id)}/reject`, { note });
    return response.data;
  },

  // Active login sessions (refresh-token families) for a user — used to show
  // "N active sessions" before signing them out. Uses user.id like approve/reject.
  getSessions: async (userId) => {
    const response = await api.get(`/users/${encodeURIComponent(userId)}/sessions`);
    return response.data;
  },

  // "Sign out user": revoke ALL the user's sessions across every SSO product.
  // Uses user.id (matches approve/reject/removeModulePermission).
  revokeSessions: async (userId, reason) => {
    const response = await api.post(`/users/${encodeURIComponent(userId)}/revoke-sessions`, { reason });
    return response.data;
  },

  resendInvite: async (username) => {
    const response = await api.post(`/users/${encodeURIComponent(username)}/resend-invite`);
    return response.data;
  },

  setPassword: async (username, password) => {
    const response = await api.post(`/users/${encodeURIComponent(username)}/set-password`, { password });
    return response.data;
  },

  resetPassword: async (username) => {
    const response = await api.post(`/users/${encodeURIComponent(username)}/reset-password`);
    return response.data;
  },

  // Tenant assignment methods
  getTenants: async (username) => {
    const response = await api.get(`/users/${encodeURIComponent(username)}/tenants`);
    return response.data;
  },

  assignTenant: async (username, tenantId, permissions) => {
    const response = await api.post(`/users/${encodeURIComponent(username)}/tenants`, { tenantId, permissions });
    return response.data;
  },

  removeTenant: async (username, tenantId) => {
    const response = await api.delete(`/users/${encodeURIComponent(username)}/tenants/${tenantId}`);
    return response.data;
  },

  // Per-module grant revocation (V276). Deletes exactly ONE module row of the
  // user's grant on the tenant — the sibling modules are untouched, unlike
  // removeTenant which drops the whole membership. 404 when the row is absent.
  removeModulePermission: async (userId, tenantId, module) => {
    const response = await api.delete(`/users/${encodeURIComponent(userId)}/tenants/${tenantId}/permissions/${module}`);
    return response.data;
  },

  // Migration methods (provisions personal workspace for legacy users)
  migrate: async (userId) => {
    const response = await api.post(`/users/${userId}/migrate`);
    return response.data;
  },

  bulkMigrate: async () => {
    const response = await api.post('/users/bulk-migrate');
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Tenant Service
// ────────────────────────────────────────────────────────────────
export const tenantService = {
  getAll: async (params = {}) => {
    const response = await api.get('/tenants', { params });
    return response.data;
  },

  // Backward-compatible alias used by older admin pages.
  listTenants: async (params = {}) => {
    const response = await api.get('/tenants', { params });
    return response.data;
  },

  getById: async (tenantId, params = {}) => {
    const response = await api.get(`/tenants/${tenantId}`, { params });
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/tenants', data);
    return response.data;
  },

  update: async (tenantId, data) => {
    const response = await api.put(`/tenants/${tenantId}`, data);
    return response.data;
  },

  delete: async (tenantId) => {
    const response = await api.delete(`/tenants/${tenantId}`);
    return response.data;
  },

  restore: async (tenantId) => {
    const response = await api.post(`/tenants/${tenantId}/restore`);
    return response.data;
  },

  // User assignment methods
  getUsers: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/users`);
    return response.data;
  },

  addUser: async (tenantId, userId, permissions) => {
    const response = await api.post(`/tenants/${tenantId}/users`, { userId, permissions });
    return response.data;
  },

  removeUser: async (tenantId, userId) => {
    const response = await api.delete(`/tenants/${tenantId}/users/${userId}`);
    return response.data;
  },

  // ── Product enrollment (V276) ──
  // POST is idempotent: enrolling an already-enrolled product returns the
  // existing row (200). DELETE is allowed on archived tenants, but disabling
  // media_console while devices still reference the tenant is a 409 carrying
  // top-level { error, code: 'DEVICES_STILL_ASSIGNED', count } — surface the
  // count, don't retry.
  enrollProduct: async (tenantId, product) => {
    const response = await api.post(`/tenants/${tenantId}/products`, { product });
    return response.data;
  },

  unenrollProduct: async (tenantId, product) => {
    const response = await api.delete(`/tenants/${tenantId}/products/${product}`);
    return response.data;
  },

  // Invitation history for a tenant (status, role, inviter, dates).
  getInvitations: async (tenantId) => {
    const response = await api.get(`/tenants/${tenantId}/invitations`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Audit Log Service (login activity monitoring)
// ────────────────────────────────────────────────────────────────
export const auditLogService = {
  getAll: async (params = {}) => {
    const response = await api.get('/audit-log', { params });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Enum Service (centralized options/lists)
// ────────────────────────────────────────────────────────────────
export const enumService = {
  getAll: async () => {
    const response = await api.get('/enums/lists/all');
    return response.data;
  },

  getByName: async (enumName) => {
    const response = await api.get(`/enums/lists/${enumName}`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// License Service (GaliGali OS Licenses)
// ────────────────────────────────────────────────────────────────
export const licenseService = {
  getAll: async (params = {}) => {
    const response = await api.get('/licenses', { params });
    return response.data;
  },

  getById: async (licenseId) => {
    const response = await api.get(`/licenses/${licenseId}`);
    return response.data;
  },

  generate: async (count, ownerType = null, ownerId = null) => {
    const response = await api.post('/licenses/generate', { count, ownerType, ownerId });
    return response.data;
  },

  assignOwner: async (licenseId, ownerType, ownerId) => {
    const response = await api.post(`/licenses/${licenseId}/assign`, { ownerType, ownerId });
    return response.data;
  },

  validate: async (licenseKey, deviceId) => {
    const response = await api.post('/licenses/validate', { licenseKey, deviceId });
    return response.data;
  },

  deactivate: async (licenseId, reason = null) => {
    const response = await api.post(`/licenses/${licenseId}/deactivate`, { reason });
    return response.data;
  },

  revoke: async (licenseId, reason) => {
    const response = await api.post(`/licenses/${licenseId}/revoke`, { reason });
    return response.data;
  },

  unrevoke: async (licenseId) => {
    const response = await api.post(`/licenses/${licenseId}/unrevoke`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// GGOS Device Service (GaliGali OS Devices - IoT Registration)
// ────────────────────────────────────────────────────────────────
export const ggosDeviceService = {
  getAll: async (params = {}) => {
    const response = await api.get('/ggos-devices', { params });
    return response.data;
  },

  getByThingName: async (thingName) => {
    const response = await api.get(`/ggos-devices/${thingName}`);
    return response.data;
  },

  getAdManifestStatus: async (thingName) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/ad-manifest-status`);
    return response.data;
  },

  getProofOfPlay: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/proof-of-play`, { params });
    return response.data;
  },

  getSnapshot: async (thingName) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/snapshot`);
    return response.data;
  },

  // H8 (Increment II): the pane's health in this cab — JOURNEY counts, WebView version, thermal, the agent's pane block.
  getPaneHealth: async (thingName, days = 7) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/pane-health`, { params: { days } });
    return response.data;
  },

  getTokenStatus: async (thingName) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/token-status`);
    return response.data;
  },

  getCommandDefinitions: async () => {
    const response = await api.get('/ggos-devices/command-definitions');
    return response.data;
  },

  getCommandHistory: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/command-history`, { params });
    return response.data;
  },

  getWarehouseAuditHistory: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/warehouse/audit-history`, { params });
    return response.data;
  },

  getPerceptionSummary: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/perception-data/summary`, { params });
    return response.data;
  },

  getPerceptionAnalytics: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/perception-data/analytics`, { params });
    return response.data;
  },

  getPerceptionEvents: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/perception-data`, { params });
    return response.data;
  },

  getPerceptionIdentity: async (thingName) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/perception-data/identity`);
    return response.data;
  },

  getPerceptionDemographics: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/perception-data/demographics`, { params });
    return response.data;
  },

  getPerceptionSessionHistory: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/perception-data/session-history`, { params });
    return response.data;
  },

  getPerceptionFaceCrops: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${encodeURIComponent(thingName)}/perception-data/face-crops`, { params });
    return response.data;
  },

  deactivate: async (thingName, reason) => {
    const response = await api.post(`/ggos-devices/${thingName}/deactivate`, { reason });
    return response.data;
  },

  getEventLogs: async (deviceId, params = {}) => {
    const response = await api.get(`/device-event-logs/by-device/${deviceId}`, { params });
    return response.data;
  },

  reactivate: async (thingName, reason) => {
    const response = await api.post(`/ggos-devices/${thingName}/reactivate`, { reason });
    return response.data;
  },

  getConnectionHistory: async (thingName, hours = 24) => {
    const response = await api.get(`/ggos-devices/${thingName}/connection-history`, { params: { hours } });
    return response.data;
  },

  getDailyHealth: async (thingName, days = 7) => {
    const response = await api.get(`/ggos-devices/${thingName}/daily-health`, { params: { days } });
    return response.data;
  },

  getDeliveryPresence: async (thingName, days = 7) => {
    const response = await api.get(`/ggos-devices/${thingName}/delivery-presence`, { params: { days } });
    return response.data;
  },

  getSignalTimeline: async (thingName, date) => {
    const response = await api.get(`/ggos-devices/${thingName}/signal-timeline`, { params: date ? { date } : {} });
    return response.data;
  },

  getSignalWindow: async (thingName, from, to) => {
    const response = await api.get(`/ggos-devices/${thingName}/signal-window`, { params: { from, to } });
    return response.data;
  },

  getWitnessDetail: async (thingName, from, to, limit = 60) => {
    const response = await api.get(`/ggos-devices/${thingName}/witness-detail`, { params: { from, to, limit } });
    return response.data;
  },

  getMobility: async (thingName, hours = 24) => {
    const response = await api.get(`/ggos-devices/${thingName}/mobility`, { params: { hours } });
    return response.data;
  },

  getScreenDelivery: async (thingName, hours = 24) => {
    const response = await api.get(`/media-screens/${thingName}/delivery`, { params: { hours } });
    return response.data;
  },

  getScreenBattery: async (thingName, hours = 24) => {
    const response = await api.get(`/media-screens/${thingName}/battery`, { params: { hours } });
    return response.data;
  },

  getMediaScreens: async (params = {}) => {
    const response = await api.get('/media-screens', { params });
    return response.data;
  },

  getTelemetryEventsSummary: async (thingName, params = {}) => {
    const response = await api.get(`/devices/${encodeURIComponent(thingName)}/events/summary`, { params });
    return response.data;
  },

  getTelemetryEvents: async (thingName, params = {}) => {
    const response = await api.get(`/devices/${thingName}/events`, { params });
    return response.data;
  },

  getIdentityMesh: async (unifiedId) => {
    const response = await api.get(`/ggos-devices/identity/${unifiedId}/mesh`);
    return response.data;
  },

  getIdentities: async (params = {}) => {
    const response = await api.get('/ggos-devices/identities', { params });
    return response.data;
  },

  getIdentityDetail: async (unifiedId, params = {}) => {
    const response = await api.get(`/ggos-devices/identity/${encodeURIComponent(unifiedId)}`, { params });
    return response.data;
  },

  triggerReconstruct: async (unifiedId) => {
    const response = await api.post(`/ggos-devices/identity/${unifiedId}/reconstruct`);
    return response.data;
  },

  getSnapshots: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${thingName}/snapshots`, { params });
    return response.data;
  },

  getFaceCropUrl: async (key) => {
    const response = await api.get('/ggos-devices/face-crop-url', { params: { key } });
    return response.data;
  },

  batchSignFaceCrops: async (keys) => {
    const response = await api.post('/ggos-devices/face-crop-urls', { keys });
    return response.data;
  },

  getOts: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${thingName}/ots`, { params });
    return response.data;
  },

  getAudience: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${thingName}/audience`, { params });
    return response.data;
  },

  getAttention: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${thingName}/attention`, { params });
    return response.data;
  },

  getReach: async (campaignId, params = {}) => {
    const response = await api.get(`/ggos-devices/reach/${campaignId}`, { params });
    return response.data;
  },

  // ── App Management ──

  getApps: async (thingName, params = {}) => {
    const response = await api.get(`/ggos-devices/${thingName}/apps`, { params });
    return response.data;
  },

  getApkUploadUrl: async (thingName, fileName, fileSize) => {
    const response = await api.post(`/ggos-devices/${thingName}/apps/upload-url`, { fileName, fileSize });
    return response.data;
  },

  sendDeviceCommand: async (thingName, operation, parameters = {}, confirmed = false) => {
    const response = await api.post(`/ggos-devices/${thingName}/commands`, {
      operation,
      parameters,
      confirmed,
    });
    return response.data;
  },

  getCommandStatus: async (thingName, commandId) => {
    const response = await api.get(`/ggos-devices/${thingName}/commands/${commandId}`);
    return response.data;
  },

  // APK Library
  getApkLibrary: async (params = {}) => {
    const response = await api.get('/ggos-devices/apk-library', { params });
    return response.data;
  },

  getApkDownloadUrl: async (apkId) => {
    const response = await api.post(`/ggos-devices/apk-library/${apkId}/download-url`);
    return response.data;
  },

  updateApkMetadata: async (apkId, fields) => {
    const response = await api.patch(`/ggos-devices/apk-library/${apkId}`, fields);
    return response.data;
  },

  installFromLibrary: async (apkId, thingName) => {
    const response = await api.post(`/ggos-devices/apk-library/${apkId}/install`, { thingName });
    return response.data;
  },

  // ── Release Management (fleet app-version observability) ──
  getFleetAppVersions: async () => {
    const response = await api.get('/ggos-devices/fleet-app-versions');
    return response.data;
  },

  getAppVersionDevices: async (packageName, versionName) => {
    const params = { package: packageName };
    if (versionName !== undefined) params.version = versionName === null ? '' : versionName;
    const response = await api.get('/ggos-devices/app-version-devices', { params });
    return response.data;
  },

  // ── On-demand APK builds (CodeBuild-backed) ──
  getRepoBranches: async () => {
    const response = await api.get('/ggos-devices/repo-branches');
    return response.data;
  },

  triggerApkBuild: async ({ branch, apps, buildType }) => {
    const response = await api.post('/ggos-devices/apk-builds', { branch, apps, buildType });
    return response.data;
  },

  getApkBuilds: async () => {
    const response = await api.get('/ggos-devices/apk-builds');
    return response.data;
  },

  getApkBuild: async (id) => {
    const response = await api.get(`/ggos-devices/apk-builds/${id}`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Bootstrap CA Service (Bootstrap Certificate Authorities)
// ────────────────────────────────────────────────────────────────
export const bootstrapCaService = {
  getAll: async (params = {}) => {
    const response = await api.get('/bootstrap-cas', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/bootstrap-cas/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/bootstrap-cas/stats');
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/bootstrap-cas', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/bootstrap-cas/${id}`, data);
    return response.data;
  },

  activate: async (id) => {
    const response = await api.post(`/bootstrap-cas/${id}/activate`);
    return response.data;
  },

  deactivate: async (id, reason) => {
    const response = await api.post(`/bootstrap-cas/${id}/deactivate`, { reason });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/bootstrap-cas/${id}`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Bootstrap Auth Logs Service (Authentication attempt logs)
// ────────────────────────────────────────────────────────────────
export const bootstrapAuthLogsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/bootstrap-auth-logs', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/bootstrap-auth-logs/${id}`);
    return response.data;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/bootstrap-auth-logs/stats', { params });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Device Event Logs Service (Device lifecycle events)
// ────────────────────────────────────────────────────────────────
export const deviceEventLogsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/device-event-logs', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/device-event-logs/${id}`);
    return response.data;
  },

  getByDevice: async (deviceId, params = {}) => {
    const response = await api.get(`/device-event-logs/by-device/${deviceId}`, { params });
    return response.data;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/device-event-logs/stats', { params });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Proxy Service (calls iot-android-api via proxy)
// ────────────────────────────────────────────────────────────────
export const proxyService = {
  get: async (path, params = {}) => {
    const response = await api.get(`/proxy/${path}`, { params });
    return response.data;
  },

  post: async (path, data, params = {}) => {
    const response = await api.post(`/proxy/${path}`, data, { params });
    return response.data;
  },

  put: async (path, data, params = {}) => {
    const response = await api.put(`/proxy/${path}`, data, { params });
    return response.data;
  },

  delete: async (path, params = {}) => {
    const response = await api.delete(`/proxy/${path}`, { params });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Billing Admin Service (proxied to billing.galigali.in)
// ────────────────────────────────────────────────────────────────
export const billingAdminService = {
  // Dashboard stats
  getStats: async (params = {}) => {
    const response = await api.get('/billing/admin/stats', { params });
    return response.data;
  },

  // List all billing accounts (paginated, searchable)
  getAccounts: async (params = {}) => {
    const response = await api.get('/billing/admin/accounts', { params });
    return response.data;
  },

  // Get single billing account detail
  getAccount: async (accountId) => {
    const response = await api.get(`/billing/admin/accounts/${accountId}`);
    return response.data;
  },

  // Get billing history timeline for an account
  getTimeline: async (accountId, params = {}) => {
    const response = await api.get(`/billing/admin/timeline/${accountId}`, { params });
    return response.data;
  },

  // Grant ad credit
  grantCredit: async (data) => {
    const response = await api.post('/billing/admin/credits', data);
    return response.data;
  },

  // Update account config (thresholds, notification hours, notes)
  updateConfig: async (accountId, data) => {
    const response = await api.put(`/billing/admin/config/${accountId}`, data);
    return response.data;
  },

  // Get billing account by tenant ID
  getAccountByTenant: async (tenantId) => {
    const response = await api.get(`/billing/admin/accounts/by-tenant/${tenantId}`);
    return response.data;
  },

  // Cross-tenant transaction list (paginated, filterable)
  getTransactions: async (params = {}) => {
    const response = await api.get('/billing/admin/transactions', { params });
    return response.data;
  },

  // Cross-tenant invoice list (paginated, filterable)
  getInvoices: async (params = {}) => {
    const response = await api.get('/billing/admin/invoices', { params });
    return response.data;
  },

  // Invoice detail with line items
  getInvoice: async (invoiceId) => {
    const response = await api.get(`/billing/admin/invoices/${invoiceId}`);
    return response.data;
  },

  // Cross-tenant credit list (paginated, filterable)
  getCredits: async (params = {}) => {
    const response = await api.get('/billing/admin/credits/list', { params });
    return response.data;
  },

  // Cross-tenant payment methods list
  getPaymentMethods: async (params = {}) => {
    const response = await api.get('/billing/admin/payment-methods', { params });
    return response.data;
  },

  // Activity feed (recent billing events across all tenants)
  getActivityFeed: async (params = {}) => {
    const response = await api.get('/billing/admin/activity-feed', { params });
    return response.data;
  },

  // Account ledger (append-only forensic audit)
  getAccountLedger: async (accountId, params = {}) => {
    const response = await api.get(`/billing/admin/accounts/${accountId}/ledger`, { params });
    return response.data;
  },

  // Retry a failed transaction
  retryTransaction: async (txnId) => {
    const response = await api.post(`/billing/admin/transactions/${txnId}/retry`);
    return response.data;
  },

  // Force-resolve a stuck transaction
  forceResolveTransaction: async (txnId, data) => {
    const response = await api.post(`/billing/admin/transactions/${txnId}/force-resolve`, data);
    return response.data;
  },

  // Revoke an active credit
  revokeCredit: async (creditId, data) => {
    const response = await api.post(`/billing/admin/credits/${creditId}/revoke`, data);
    return response.data;
  },

  // Webhook logs (cross-tenant)
  getWebhooks: async (params = {}) => {
    const response = await api.get('/billing/admin/webhooks', { params });
    return response.data;
  },

  // Webhook detail with full payload
  getWebhook: async (webhookId) => {
    const response = await api.get(`/billing/admin/webhooks/${webhookId}`);
    return response.data;
  },

  // Reprocess a failed webhook
  reprocessWebhook: async (webhookId) => {
    const response = await api.post(`/billing/admin/webhooks/${webhookId}/reprocess`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Moments API Key Management (super admin)
// ────────────────────────────────────────────────────────────────
export const momentsAdminService = {
  // List all keys for a tenant
  listKeys: async (tenantId) => {
    const response = await api.get('/moments/keys', { headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // List all keys across tenants (super admin)
  listAllKeys: async () => {
    const response = await api.get('/moments/keys/all');
    return response.data;
  },

  // Get single key detail
  getKey: async (tenantId, keyId) => {
    const response = await api.get(`/moments/keys/${keyId}`, { headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // Create a new API key for a tenant
  createKey: async (tenantId, data) => {
    const response = await api.post('/moments/keys', data, { headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // Update key metadata
  updateKey: async (tenantId, keyId, data) => {
    const response = await api.patch(`/moments/keys/${keyId}`, data, { headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // Revoke a key
  revokeKey: async (tenantId, keyId, reason) => {
    const response = await api.post(`/moments/keys/${keyId}/revoke`, { reason }, { headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // Rotate a key (revoke + create new with same settings)
  rotateKey: async (tenantId, keyId) => {
    const response = await api.post(`/moments/keys/${keyId}/rotate`, {}, { headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // Key usage stats (30 days)
  getKeyUsage: async (tenantId, keyId) => {
    const response = await api.get(`/moments/keys/${keyId}/usage`, { headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // Tenant-wide usage stats
  getUsageStats: async (tenantId, params = {}) => {
    const response = await api.get('/moments/keys/usage/stats', { params, headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },

  // Billing summary per key
  getBillingSummary: async (tenantId, params = {}) => {
    const response = await api.get('/moments/keys/billing/summary', { params, headers: { 'X-Tenant-Id': tenantId } });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// Moments Billing Plans (super admin)
// ────────────────────────────────────────────────────────────────
export const momentsPlanService = {
  listPlans: async (params = {}) => {
    const response = await api.get('/moments/plans', { params });
    return response.data;
  },
  getPlan: async (planId) => {
    const response = await api.get(`/moments/plans/${planId}`);
    return response.data;
  },
  createPlan: async (data) => {
    const response = await api.post('/moments/plans', data);
    return response.data;
  },
  updatePlan: async (planId, data) => {
    const response = await api.patch(`/moments/plans/${planId}`, data);
    return response.data;
  },
  archivePlan: async (planId) => {
    const response = await api.delete(`/moments/plans/${planId}`);
    return response.data;
  },
  listAssignments: async (params = {}) => {
    const response = await api.get('/moments/plans/assignments', { params });
    return response.data;
  },
  assignPlan: async (data) => {
    const response = await api.post('/moments/plans/assignments', data);
    return response.data;
  },
  updateAssignment: async (assignmentId, data) => {
    const response = await api.patch(`/moments/plans/assignments/${assignmentId}`, data);
    return response.data;
  },
  cancelAssignment: async (assignmentId) => {
    const response = await api.delete(`/moments/plans/assignments/${assignmentId}`);
    return response.data;
  },
  getCycle: async (tenantId) => {
    const response = await api.get(`/moments/plans/cycles/${tenantId}`);
    return response.data;
  },
  getCycleHistory: async (tenantId, params = {}) => {
    const response = await api.get(`/moments/plans/cycles/${tenantId}/history`, { params });
    return response.data;
  },
  getAiCycle: async (tenantId) => {
    const response = await api.get(`/moments/plans/cycles/${tenantId}/ai`);
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────
// City Management Service (hex grid, sentinel traffic, pipelines)
// ────────────────────────────────────────────────────────────────
export const cityService = {
  getAll: async (params = {}) => {
    const response = await api.get('/cities', { params });
    return response.data;
  },
  getById: async (cityId, params = {}) => {
    const response = await api.get(`/cities/${cityId}`, { params });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/cities', data);
    return response.data;
  },
  update: async (cityId, data) => {
    const response = await api.put(`/cities/${cityId}`, data);
    return response.data;
  },
  delete: async (cityId) => {
    const response = await api.delete(`/cities/${cityId}`);
    return response.data;
  },
  restore: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/restore`);
    return response.data;
  },

  // Sentinel route management
  addRoute: async (cityId, data) => {
    const response = await api.post(`/cities/${cityId}/routes`, data);
    return response.data;
  },
  deleteRoute: async (cityId, routeId) => {
    const response = await api.delete(`/cities/${cityId}/routes/${routeId}`);
    return response.data;
  },

  // Pipeline triggers
  triggerSeed: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/seed`);
    return response.data;
  },
  triggerEnrich: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/enrich`);
    return response.data;
  },
  triggerCalibrate: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/calibrate`);
    return response.data;
  },
  triggerPoll: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/poll`);
    return response.data;
  },

  // Data views
  getTrafficDistribution: async (cityId, params = {}) => {
    const response = await api.get(`/cities/${cityId}/traffic-distribution`, { params });
    return response.data;
  },
  getSentinelReadings: async (cityId, params = {}) => {
    const response = await api.get(`/cities/${cityId}/sentinel-readings`, { params });
    return response.data;
  },
  getCostEstimate: async (cityId) => {
    const response = await api.get(`/cities/${cityId}/cost-estimate`);
    return response.data;
  },
  getPipelineStats: async (cityId) => {
    const response = await api.get(`/cities/${cityId}/pipeline-stats`);
    return response.data;
  },
  getSignalFreshness: async (cityId) => {
    const response = await api.get(`/cities/${cityId}/signal-freshness`);
    return response.data;
  },
  getDataQuality: async (cityId) => {
    const response = await api.get(`/cities/${cityId}/data-quality`);
    return response.data;
  },

  // Additional pipeline triggers
  triggerWeather: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/weather`);
    return response.data;
  },
  triggerAqi: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/aqi`);
    return response.data;
  },
  triggerScene: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/scene`);
    return response.data;
  },
  triggerSceneRes10: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/scene-res10`);
    return response.data;
  },
  triggerDeriveLabels: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/derive-labels`);
    return response.data;
  },
  triggerManagePartitions: async (cityId) => {
    const response = await api.post(`/cities/${cityId}/pipelines/manage-partitions`);
    return response.data;
  },

  // FAANG city onboarding
  triggerTransit: async (cityId, source = 'both') => {
    const response = await api.post(`/cities/${cityId}/pipelines/transit`, { source });
    return response.data;
  },
  triggerGoLive: async (cityId, body = {}) => {
    const response = await api.post(`/cities/${cityId}/pipelines/go-live`, body);
    return response.data;
  },
  getOnboardingStatus: async (cityId) => {
    const response = await api.get(`/cities/${cityId}/pipelines/status`);
    return response.data;
  },
  getSuperAdminMonitoring: async () => {
    const response = await api.get(`/cities/super-admin/monitoring`);
    return response.data;
  },

  // Redis admin
  getRedisStats: async () => {
    const response = await api.get('/cities/admin/redis/stats');
    return response.data;
  },
  flushCityRedis: async (cityId) => {
    const response = await api.post(`/cities/admin/redis/flush-city/${cityId}`);
    return response.data;
  },
  sampleRedisKeys: async (pattern = 'hex:*', count = 10) => {
    const response = await api.get('/cities/admin/redis/sample', { params: { pattern, count } });
    return response.data;
  },

  // Hex Map
  getHexMap: async (cityId, { resolution, south, west, north, east, colorBy, signal }) => {
    const response = await api.get(`/cities/${cityId}/hex-map`, {
      params: { resolution, south, west, north, east, colorBy, signal },
    });
    return response.data;
  },
  getHexDetail: async (cityId, h3Index) => {
    const response = await api.get(`/cities/${cityId}/hexes/${h3Index}`);
    return response.data;
  },
  getHexHistory: async (cityId, h3Index, { signal, hours, limit } = {}) => {
    const response = await api.get(`/cities/${cityId}/hexes/${h3Index}/history`, {
      params: { signal, hours, limit },
    });
    return response.data;
  },
};

// Speech-source labelling (V310) — the field-Hindi human pass.
export const speechLabelService = {
  enqueue: async (batchTag, opts = {}) => {
    const response = await api.post('/speech-labels/enqueue', { batch_tag: batchTag, ...opts });
    return response.data;
  },
  queue: async (pass, batch, limit = 20) => {
    const response = await api.get('/speech-labels/queue', { params: { pass, batch: batch || undefined, limit } });
    return response.data;
  },
  submit: async (segmentId, pass, label, rationale) => {
    const response = await api.post('/speech-labels', { segment_id: segmentId, pass, label, rationale });
    return response.data;
  },
  remove: async (segmentId, pass) => {
    const response = await api.delete(`/speech-labels/${segmentId}/${pass}`);
    return response.data;
  },
  stats: async (batch) => {
    const response = await api.get('/speech-labels/stats', { params: { batch: batch || undefined } });
    return response.data;
  },
};

// Read-only cross-tenant campaign drill-down.
export const campaignService = {
  list: async (params) => {
    const response = await api.get('/campaigns', { params });
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },
};

// Cross-tenant BM moderation (decisions proxied to the Brands engine).
export default api;
